import { initializeApp } from "firebase/app";
import {
  QueryConstraint,
  QueryDocumentSnapshot,
  collection,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyAzKqDnThFQPiA_r3PKObPVAVpdeiV1IBg",
  authDomain: "planning-center-db.firebaseapp.com",
  databaseURL: "https://planning-center-db-default-rtdb.firebaseio.com",
  projectId: "planning-center-db",
  storageBucket: "planning-center-db.appspot.com",
  messagingSenderId: "154464201379",
  appId: "1:154464201379:web:a238f105995f772c31ad16"
};

const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);

const ATTENDEES_COLLECTION = "Attendees";
const CHECKIN_DATES_COLLECTION = "check-in-dates";
const PRINTERS_COLLECTION = "printers";
const GROUPS_COLLECTION = "groups";
const ROTATIONS_COLLECTION = "rotations";
const USERS_COLLECTION = "users";
const EVENTS_COLLECTION = "events";
const ACTIVITY_LOG_COLLECTION = "activity-log";
const CUSTOM_FIELDS_COLLECTION = "custom-fields";

export const attendeesCollectionRef = collection(db, ATTENDEES_COLLECTION);
export const checkInDatesCollectionRef = collection(db, CHECKIN_DATES_COLLECTION);
export const printersCollectionRef = collection(db, PRINTERS_COLLECTION);
export const groupsCollectionRef = collection(db, GROUPS_COLLECTION);
export const rotationsCollectionRef = collection(db, ROTATIONS_COLLECTION);
export const usersCollectionRef = collection(db, USERS_COLLECTION);
export const eventsCollectionRef = collection(db, EVENTS_COLLECTION);
export const activityLogCollectionRef = collection(db, ACTIVITY_LOG_COLLECTION);
export const customFieldsCollectionRef = collection(db, CUSTOM_FIELDS_COLLECTION);

// Events, printers, and custom-fields are global (not per-event); their
// queries stay static.
//
// custom-fields intentionally does NOT filter `is_deleted` server-side.
// The collection is tiny (admins curate a handful of fields), and a
// server-side filter would (a) require a composite index and (b) silently
// drop legacy docs that predate `is_deleted` (Firestore's equality filter
// excludes documents missing the field entirely). `firebase-context`
// filters out soft-deleted entries on the client instead, which also
// treats legacy docs as not-deleted.
export const getAllPrintersQuery = query(printersCollectionRef, orderBy("name"));
export const getAllEventsQuery = query(eventsCollectionRef, orderBy("name"));
export const getAllCustomFieldsQuery = query(
  customFieldsCollectionRef,
  orderBy("name"),
);

// Per-event query factories. The active event's ref is the partitioning key;
// `firebase-context` re-subscribes whenever it changes.
export const getAttendeesForEventQuery = (eventRef: string) =>
  query(
    attendeesCollectionRef,
    where("eventRef", "==", eventRef),
    where("is_deleted", "==", false),
    orderBy("first_name"),
  );

export const getCheckInDatesForEventQuery = (eventRef: string) =>
  query(
    checkInDatesCollectionRef,
    where("eventRef", "==", eventRef),
    orderBy("date"),
  );

export const getGroupsForEventQuery = (eventRef: string) =>
  query(
    groupsCollectionRef,
    where("eventRef", "==", eventRef),
    orderBy("groupName"),
  );

export const getRotationsForEventQuery = (eventRef: string) =>
  query(
    rotationsCollectionRef,
    where("eventRef", "==", eventRef),
    orderBy("name"),
  );

// -----------------------------------------------------------------------------
// Activity log
// -----------------------------------------------------------------------------
// Single source of truth for activity-log queries. Every read path goes
// through here so we can't accidentally issue an unbounded `getDocs` against
// a collection that grows monotonically. `pageSize` defaults to 200.

export const ACTIVITY_LOG_PAGE_SIZE = 200;

export type ActivityLogQueryOptions = {
  eventRef?: string;
  actorUid?: string;
  pageSize?: number;
  startAfter?: QueryDocumentSnapshot;
};

export const getActivityLogQuery = ({
  eventRef,
  actorUid,
  pageSize = ACTIVITY_LOG_PAGE_SIZE,
  startAfter: cursor,
}: ActivityLogQueryOptions = {}) => {
  // Orders by `timestamp` descending. Equality filters use a composite index;
  // see `firestore.indexes.json`. When both `eventRef` and `actorUid` are set,
  // both `where` clauses apply so pagination stays correct (no client-side
  // filter + broken `startAfter`).
  const clauses: QueryConstraint[] = [];
  if (eventRef && actorUid) {
    clauses.push(where("eventRef", "==", eventRef));
    clauses.push(where("actorUid", "==", actorUid));
  } else if (eventRef) {
    clauses.push(where("eventRef", "==", eventRef));
  } else if (actorUid) {
    clauses.push(where("actorUid", "==", actorUid));
  }
  clauses.push(orderBy("timestamp", "desc"));
  if (cursor) clauses.push(startAfter(cursor));
  clauses.push(limit(pageSize));
  return query(activityLogCollectionRef, ...clauses);
};
